import merge from 'lodash/merge';
import { Observable, zip, merge as rxMerge } from 'rxjs';
import { map, share, ignoreElements, concat } from 'rxjs/operators';

import UnifiedPlayer from './UnifiedPlayer';

/**
 * Creates a new shared observable that emits next when all the observables from plugins emit
 * next, with the values merged together. The returned observable emits error when any of the
 * plugins emits an error, and it emits complete when all of the plugins emit complete.
 * @private
 * @param  {Plugin[]}            plugins List of plugins.
 * @param  {Observable} media$  Media observable that emits next on updateMedia.
 * @param  {UnifiedPlayer}       player  UnifiedPlayer instance.
 * @return {Observable}         Observable emitting the combined result from plugins.
 */
export function createPluginObservable(
  plugins: any,
  media$: Observable<any>,
  player: UnifiedPlayer
) {
  // Create an array of returned streams from each plugin, with an always active default plugin
  // that mirrors the input from `media$`. The streams are then shared since they need to be
  // subscribed to twice; Once when they are zipped together for calls to `media$.next()`and once
  // when they are merged together to wait for all of them to complete on `media$.complete()`.
  const resultPlugins = [(stream: any, _: any) => stream]
    .concat(plugins)
    .map(plugin => plugin(media$, player))
    .filter(result => result instanceof Observable)
    .map(stream => stream.pipe(share()));

  // When all of the `resultPlugins` emits an item, combine them into a single object and emit
  // next. If complete is called on the `media$`, the zipped observable ends immediately. To make
  // sure the `plugin$` only ends once all plugins are complete, the `resultPlugins` are merged
  // together and then concatenated to the zipped observable. Since only the complete message is
  // needed, all elements of the merged observable is ignored. The `plugin$´ needs to be shared
  // since it is being subscribed to by the `adapter` and in `updateMedia()` and `destroy()`.
  return zip(...resultPlugins).pipe(
    map(items => merge({}, ...items)),
    concat(rxMerge(...resultPlugins).pipe(ignoreElements())),
    share()
  );
}
